import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import _ from 'lodash';

import { EXP_STATUS_MAP } from '../../utils/constants/financeStatus.constant';
import { brandHex } from '../../utils/orgConfig';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

dayjs.locale('th');
dayjs.extend(buddhistEra);
dayjs.extend(LocalizedFormat);

const fmtMoney = (n) =>
  Number(n || 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,');

// วิเคราะห์จากข้อมูลจริงใน DB (สุ่ม 391 รายการ): "รายการ" ยาวเฉลี่ย 75 ตัวอักษร
// (มัธยฐาน 48, สูงสุด 233 — มีรายการที่แจกแจงหลายบรรทัด) จึงให้คอลัมน์นี้กว้างที่สุด
// และปล่อยให้ตัดบรรทัดเอง (wrap) ส่วนคอลัมน์อื่นสั้นกว่ามาก: รหัสคงที่ 11 ตัว,
// ผู้รับเงินเฉลี่ย 25 ตัว (สูงสุด 47), หมวดเฉลี่ย 18 ตัว (สูงสุด 33)
// รวมความกว้าง 8 คอลัมน์ = 530pt เว้น buffer ไว้จากพื้นที่ใช้งานจริง 555pt ของ A4
// แนวตั้ง (595pt - margin 20+20) กันคอลัมน์สุดท้ายล้นออกนอกหน้ากระดาษ
const COL_WIDTHS = [40, 46, 143, 65, 59, 72, 53, 52];

// สร้างแถวหัวคอลัมน์ "ใหม่" ทุกครั้งที่เรียก (ห้ามใช้ array/object เดิมซ้ำข้ามตาราง)
// เพราะ pdfMake แก้ไข object ของแต่ละ cell ค้างไว้ข้างในตอนคำนวณ layout —
// ถ้าเอา object เดิมไปใช้ซ้ำในตารางที่ 2 เป็นต้นไป มันจะเรนเดอร์เป็นแถบว่างไม่มีตัวหนังสือ
const buildHeaderRow = () =>
  ['วันที่', 'รหัส', 'รายการ', 'หมวด', 'ผู้เบิก', 'ผู้รับเงิน', 'จำนวน', 'สถานะ'].map(
    (text) => ({
      text,
      fillColor: brandHex(700),
      color: '#ffffff',
      bold: true,
      fontSize: 7,
      alignment: 'center',
      margin: [0, 3, 0, 3],
    }),
  );

const buildRow = (exp, isEven) => {
  const st = EXP_STATUS_MAP[exp?.status] || EXP_STATUS_MAP.PENDING;
  const empName = exp?.employee
    ? formatEmployeeThaiName(exp.employee)
    : '-';
  const cellSx = {
    fontSize: 7,
    alignment: 'center',
    fillColor: isEven ? '#f8f8fc' : '#ffffff',
    margin: [0, 2, 0, 2],
  };
  return [
    { ...cellSx, text: exp?.date ? dayjs(exp.date).format('DD/MM/BB') : '-' },
    { ...cellSx, text: exp?.code || '-', color: '#64748b' },
    { ...cellSx, text: exp?.name || '-', alignment: 'left' },
    { ...cellSx, text: exp?.budget?.name || '-', color: '#64748b' },
    { ...cellSx, text: empName },
    { ...cellSx, text: exp?.payee?.name || '-', color: '#64748b' },
    { ...cellSx, text: fmtMoney(exp?.price), bold: true },
    {
      ...cellSx,
      text: [{ text: st.label, color: st.color, bold: true, fontSize: 6.5 }],
    },
  ];
};

// แถวชื่องบประมาณ (แถบสีม่วงอ่อน) + แถวหัวคอลัมน์ ต้อง "สร้างใหม่" ทุกครั้งที่เรียก
// (ห้ามใช้ object เดิมซ้ำ — ดูเหตุผลที่ buildHeaderRow) แล้วใส่ไว้ใน headerRows: 2
// ของตารางเดียวกับข้อมูล เพื่อให้ pdfMake ทำซ้ำทั้งแถบชื่อ+หัวคอลัมน์ให้เองอัตโนมัติ
// ทุกครั้งที่ตารางของกลุ่มนี้ยาวข้ามหน้า
const buildTitleRow = (budgetLabel, count) => [
  {
    text: budgetLabel,
    colSpan: 7,
    fillColor: brandHex(50),
    color: brandHex(700),
    bold: true,
    fontSize: 8.5,
    margin: [4, 4, 0, 4],
    border: [false, false, false, false],
  },
  {}, {}, {}, {}, {}, {},
  {
    text: `${count} รายการ`,
    fillColor: brandHex(50),
    color: brandHex(700),
    bold: true,
    fontSize: 7,
    alignment: 'right',
    margin: [0, 4, 4, 4],
    border: [false, false, false, false],
  },
];

const buildGroupDataTable = (label, rows, subtotal) => ({
  margin: [0, 0, 0, 10],
  table: {
    headerRows: 2,
    widths: COL_WIDTHS,
    body: [
      buildTitleRow(label, rows.length),
      buildHeaderRow(),
      ...rows.map((exp, i) => buildRow(exp, i % 2 === 1)),
      [
        {
          text: 'รวมยอดหมวดนี้',
          colSpan: 6,
          alignment: 'right',
          bold: true,
          fontSize: 7.5,
          fillColor: '#f3f4f6',
          border: [false, true, false, false],
          margin: [0, 4, 4, 4],
        },
        {}, {}, {}, {}, {},
        {
          text: fmtMoney(subtotal),
          colSpan: 2,
          alignment: 'right',
          bold: true,
          fontSize: 8,
          fillColor: '#f3f4f6',
          color: brandHex(700),
          border: [false, true, false, false],
          margin: [0, 4, 4, 4],
        },
        {},
      ],
    ],
  },
  layout: {
    hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0),
    vLineWidth: () => 0.5,
    hLineColor: () => '#c7cbf5',
    vLineColor: () => '#c7cbf5',
    // ปิด padding เริ่มต้นของ pdfMake (default 4pt ซ้าย-ขวา) เพราะแต่ละ cell
    // กำหนด margin ของตัวเองอยู่แล้ว — ถ้าไม่ปิดจะซ้อนกัน 2 ชั้นจนคอลัมน์ล้น
    paddingLeft: () => 1,
    paddingRight: () => 1,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  },
});

const projectExpensesReport = (expensesList, project) => {
  const rows = expensesList || [];
  const grouped = _.groupBy(
    rows,
    (r) => r?.budget?.id || r?.budget?._id || 'no_budget',
  );

  const groupKeys = Object.keys(grouped).sort((a, b) => {
    const ba = grouped[a][0]?.budget;
    const bb = grouped[b][0]?.budget;
    const na = `${ba?.prefix || ''}${ba?.budget_number || ''}`;
    const nb = `${bb?.prefix || ''}${bb?.budget_number || ''}`;
    return na.localeCompare(nb, 'th');
  });

  const grandTotal = _.sumBy(rows, (r) => Number(r?.price) || 0);

  const groupBlocks = groupKeys.map((key) => {
    const groupRows = grouped[key];
    const b = groupRows[0]?.budget;
    const label = b
      ? `${b?.prefix || ''}${b?.budget_number || ''} — ${b?.name || 'ไม่ระบุชื่องบประมาณ'}`
      : 'ไม่ระบุงบประมาณ';
    const subtotal = _.sumBy(groupRows, (r) => Number(r?.price) || 0);
    return buildGroupDataTable(label, groupRows, subtotal);
  });

  const content =
    rows.length === 0
      ? [
          {
            text: 'ยังไม่มีรายการเบิก',
            alignment: 'center',
            color: '#9ca3af',
            fontSize: 11,
            margin: [0, 40, 0, 0],
          },
        ]
      : groupBlocks;

  const documentRef = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [20, 85, 20, 45],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: 9,
    },
    info: {
      title: 'Project Expenses Report',
      subject: 'Project Expenses Report',
    },
    header: [
      {
        margin: [20, 12, 20, 0],
        stack: [
          {
            text: 'รายงานรายการเบิกจ่ายตามโครงการ',
            fontSize: 13,
            bold: true,
            color: '#1a1a1a',
            alignment: 'center',
          },
          {
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: [
                      { text: `${project?.project_number || '-'}  ` , bold: true, color: brandHex(700) },
                      { text: project?.name || '-', bold: true },
                    ],
                    fontSize: 10,
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: `ลูกค้า: ${project?.client_id?.name || project?.customer || '-'}`,
                    fontSize: 8,
                    color: '#64748b',
                    margin: [0, 2, 0, 0],
                  },
                ],
              },
              {
                width: 'auto',
                stack: [
                  {
                    text: `ยอดรวมทั้งหมด`,
                    fontSize: 8,
                    color: '#64748b',
                    alignment: 'right',
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: `${fmtMoney(grandTotal)} บาท`,
                    fontSize: 13,
                    bold: true,
                    color: '#d97706',
                    alignment: 'right',
                  },
                ],
              },
            ],
            margin: [0, 4, 0, 0],
          },
        ],
      },
    ],
    content,
    footer(currentPage, pageCount) {
      return {
        margin: [20, 0],
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              {
                text: `พิมพ์เมื่อ: ${dayjs().format('DD/MM/YYYY HH:mm')} น.`,
                fontSize: 7,
                color: '#9ca3af',
                alignment: 'left',
                border: [false, false, false, false],
              },
              {
                text: `หน้า ${currentPage} / ${pageCount}`,
                alignment: 'right',
                fontSize: 7,
                color: '#9ca3af',
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: 'noBorders',
      };
    },
  };

  pdfMake.createPdf(documentRef).open();
};

export default projectExpensesReport;
